/* sugar:assets/lazy-images.js@2.2.6 */
/*
 * Preload lazy images slightly before they enter the viewport.
 * Native loading="lazy" thresholds vary across browsers and can feel late
 * when script payload delays evaluation. This observer bumps images to eager
 * ~1500px before they become visible, then stops watching them.
 *
 * Icons below the first screen (snippets/icon.liquid marks them
 * data-icon-lazy="<file>") wait here too; see the icon section further down.
 */
(function () {
  if (!('IntersectionObserver' in window)) {
    // Nothing can wait for the screen without an observer: show every icon now.
    var all = document.createElement('style');
    all.textContent = '.sugar-icon[data-icon-lazy]{-webkit-mask-image:var(--icon-url);mask-image:var(--icon-url)}';
    document.head.appendChild(all);
    return;
  }

  // How far below the viewport the browser starts a lazy image on its own
  // (measured 2026-09-25): Chrome 1250px, or 2500px on a 3G-or-slower
  // connection; WebKit exactly one viewport height (Safari, and every iOS
  // browser, which all run on WebKit). Anything else gets Chrome's 4G figure.
  var ua = navigator.userAgent;
  var webkit = /AppleWebKit/.test(ua) && !/Chrome|Chromium|Edg/.test(ua);
  function nativeThreshold() {
    if (webkit) return window.innerHeight || document.documentElement.clientHeight;
    var c = navigator.connection;
    if (c && c.effectiveType && c.effectiveType !== '4g') return 2500;
    return 1250;
  }

  // A responsive image declares its width in viewport terms because the
  // template cannot know the column it lands in; a grid cell at a third of the
  // page would fetch a full-width file. So the width it actually got is pinned
  // as its sizes, once it is laid out and before the browser picks a candidate
  // on its own. An image already inside the browser's own threshold (on the
  // page at load, or just revealed by a slider or gallery) has a candidate in
  // flight; re-pinning it would start a second download of the same image, so
  // it keeps the candidate its sizes picked.
  // The rect an observer reports is a frame old by the time its callback runs,
  // so during a fast scroll an image can cross the browser's own line in
  // between: only images still a margin beyond it are pinned.
  var SAFETY = 300;
  var pinMargin = nativeThreshold() + SAFETY + 600;
  var pinner = new IntersectionObserver(
    function (entries) {
      for (var i = 0; i < entries.length; i++) {
        var entry = entries[i];
        if (!entry.isIntersecting) continue;
        var img = entry.target;
        pinner.unobserve(img);
        if (!img.hasAttribute('srcset') || !img.hasAttribute('sizes')) continue;
        var rect = entry.boundingClientRect;
        var viewport = window.innerHeight || document.documentElement.clientHeight;
        if (rect.top - viewport <= nativeThreshold() + SAFETY) continue;
        if (rect.width > 0) img.sizes = Math.ceil(rect.width) + 'px';
      }
    },
    { rootMargin: '0px 0px ' + pinMargin + 'px 0px' }
  );

  function fetchNow(img) {
    img.loading = 'eager';
    // A <picture> picks its file by screen width: eager loads that one, and a copy built
    // from the <img> alone would also fetch the other screen's file.
    if (img.complete || img.parentElement.localName === 'picture') return;
    var srcset = img.getAttribute('srcset');
    var src = img.getAttribute('src');
    if (!srcset && !src) return;
    var preload = new Image();
    if (srcset) {
      preload.sizes = img.getAttribute('sizes') || '';
      preload.srcset = srcset;
    }
    if (src) preload.src = src;
  }

  var io = new IntersectionObserver(
    function (entries) {
      for (var i = 0; i < entries.length; i++) {
        if (!entries[i].isIntersecting) continue;
        io.unobserve(entries[i].target);
        fetchNow(entries[i].target);
        if (pageLoaded) track(entries[i].target, false);
      }
    },
    { rootMargin: '0px 0px 1500px 0px' }
  );

  var later = window.requestIdleCallback || function (fn) { return setTimeout(fn, 200); };
  var pageLoaded = document.readyState === 'complete';

  // A slider or swipe strip clips the slides it has moved out of view, and an
  // observer (the browser's own lazy loading included) never sees a clipped
  // picture coming: the next slide only started downloading once a visitor had
  // moved to it, and arrived blank. So once the page has loaded, a row that
  // nears the screen fetches every slide's pictures and video stills, those
  // nearest the view first, and whatever it gains later (a looping slider's
  // copies, more reviews). These are the files the slides would have asked for
  // on arrival, only earlier. A visitor saving data keeps the old behaviour.
  var conn = navigator.connection;
  var spare = !(conn && (conn.saveData || /2g/.test(conn.effectiveType || '')));
  var rowDue = [];
  var rowTimer = false;
  var rowIO = new IntersectionObserver(
    function (entries) {
      for (var i = 0; i < entries.length; i++) {
        if (!entries[i].isIntersecting) continue;
        rowIO.unobserve(entries[i].target);
        wantRow(entries[i].target);
      }
    },
    { rootMargin: '1500px 0px' }
  );
  function wantRow(row) {
    if (rowDue.indexOf(row) < 0) rowDue.push(row);
    if (!pageLoaded || rowTimer) return;
    rowTimer = true;
    later(warmRows, { timeout: 1000 });
  }
  function watchRow(row) {
    if (!spare) return;
    if (row.__swRow) {
      if (row.__swWarm) wantRow(row);
      return;
    }
    row.__swRow = true;
    rowIO.observe(row);
  }
  // Downloads two at a time, nearest the view first across every row, and
  // waits while the page is fetching what the visitor is scrolling toward, so a
  // slow connection always serves that first.
  var queue = [];
  var busy = 0;
  var near = 0;
  function pump() {
    while (busy < 2 && !near && queue.length) {
      var el = queue.shift().el;
      // Already asked for: the screen, or the slider itself, got there first.
      if (!el.isConnected || el.loading !== 'lazy') continue;
      io.unobserve(el);
      pinner.unobserve(el);
      fetchNow(el);
      track(el, true);
    }
  }
  function track(el, warm) {
    if (el.complete) return;
    var done = false;
    if (warm) busy++;
    else near++;
    function free() {
      if (done) return;
      done = true;
      el.removeEventListener('load', free);
      el.removeEventListener('error', free);
      if (warm) busy--;
      else near--;
      pump();
    }
    el.addEventListener('load', free);
    el.addEventListener('error', free);
    // A picture taken off the page never says it is done.
    setTimeout(free, 10000);
  }
  // Reads every row and everything still waiting, then queues and starts.
  function warmRows() {
    rowTimer = false;
    var rows = rowDue;
    rowDue = [];
    var viewport = window.innerHeight || document.documentElement.clientHeight;
    var waiting = queue;
    queue = [];
    for (var i = 0; i < rows.length; i++) {
      rows[i].__swWarm = true;
      var found = rows[i].isConnected ? rows[i].querySelectorAll('img[loading="lazy" i], video[data-poster]:not([poster])') : [];
      for (var j = 0; j < found.length; j++) waiting.push({ el: found[j], row: rows[i] });
    }
    var posters = [];
    for (var k = 0; k < waiting.length; k++) {
      var job = waiting[k];
      // Nothing is drawn for a picture this screen size hides.
      if (job.el.__swQueued || !job.el.isConnected || !job.el.getClientRects().length) continue;
      var view = ((job.row.classList.contains('splide') && job.row.querySelector('.splide__track')) || job.row).getBoundingClientRect();
      var r = job.el.getBoundingClientRect();
      job.d = Math.max(0, view.top - viewport, -view.bottom) + Math.max(0, view.left - r.right, r.left - view.right, view.top - r.bottom, r.top - view.bottom);
      job.el.__swQueued = true;
      if (job.el.tagName === 'VIDEO') posters.push(job.el);
      else queue.push(job);
    }
    for (var q = 0; q < queue.length; q++) queue[q].el.__swQueued = false;
    queue.sort(function (a, b) { return a.d - b.d; });
    for (var v = 0; v < posters.length; v++) posters[v].poster = posters[v].getAttribute('data-poster');
    pump();
  }
  // Every slider is a row. Any other (a swipe strip, a marquee) is found by its
  // pictures once the page has loaded: one that sits mostly beside the box of an
  // ancestor that clips it sideways. Something closed (a row, a menu) hides its
  // pictures below an edge instead, and keeps waiting for the screen. Reads them
  // all, then watches the rows found.
  var unsorted = [];
  var sortTimer = false;
  function sortSoon(els) {
    if (!spare) return;
    for (var i = 0; i < els.length; i++) unsorted.push(els[i]);
    if (!pageLoaded || sortTimer) return;
    sortTimer = true;
    later(sortRows, { timeout: 2000 });
  }
  function sortRows() {
    sortTimer = false;
    var els = unsorted;
    unsorted = [];
    var rows = [];
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      if (!el.isConnected || el.closest('.splide') || !el.getClientRects().length) continue;
      if (el.tagName === 'IMG' ? el.loading !== 'lazy' : el.hasAttribute('poster')) continue;
      var r = el.getBoundingClientRect();
      if (!r.width || !r.height) continue;
      for (var p = el.parentElement; p && p !== document.body; p = p.parentElement) {
        var b = p.getBoundingClientRect();
        if (Math.min(r.right, b.right) - Math.max(r.left, b.left) < r.width / 2 && getComputedStyle(p).overflowX !== 'visible') {
          if (Math.min(r.bottom, b.bottom) - Math.max(r.top, b.top) >= r.height / 2 && rows.indexOf(p) < 0) rows.push(p);
          break;
        }
        if (p.classList.contains('shopify-section')) break;
      }
    }
    for (var k = 0; k < rows.length; k++) watchRow(rows[k]);
  }
  function rowsAfterLoad() {
    pageLoaded = true;
    if (rowDue.length && !rowTimer) {
      rowTimer = true;
      later(warmRows, { timeout: 2000 });
    }
    sortSoon(document.querySelectorAll('img[loading="lazy" i], video[data-poster]:not([poster])'));
  }

  function watch(img) {
    pinner.observe(img);
    io.observe(img);
  }

  // Icons below the first screen paint nothing (base.css) until their file is
  // here. One already on screen before the page has loaded (a short page, a
  // sticky bar) is fetched at once; the rest wait for the load event, so they
  // never share the connection with the first screen's pictures, fonts and
  // icons, and then arrive together, ready for whatever scrolls or opens next.
  // Each file is fetched once, through a preload the icon's own mask reuses
  // (WebKit downloads an <img> or new Image() a second time for a mask), and
  // one rule then shows every icon of that file: those on the page and any a
  // slider, marquee or template adds later. A file that fails hides its icons,
  // as a missing token does on the first screen.
  var iconSheet = null;
  var iconFiles = {};
  var iconLoaded = document.readyState === 'complete';
  function iconRule(css) {
    if (!iconSheet) {
      var style = document.createElement('style');
      document.head.appendChild(style);
      iconSheet = style.sheet;
    }
    try {
      iconSheet.insertRule(css, iconSheet.cssRules.length);
    } catch (e) {}
  }
  function iconFetch(file, url, shown) {
    iconFiles[file] = true;
    var sel = '.sugar-icon[data-icon-lazy="' + file.replace(/["\\]/g, '\\$&') + '"]';
    // An icon inside a closed tab, row or drawer cannot use a preload yet, and an
    // unused preload draws a console warning; an image warms the cache instead.
    var loader = shown ? document.createElement('link') : new Image();
    loader.crossOrigin = 'anonymous';
    loader.onload = function () {
      iconRule(sel + '{-webkit-mask-image:var(--icon-url);mask-image:var(--icon-url)}');
    };
    loader.onerror = function () {
      iconRule(sel + '{display:none!important}');
    };
    if (shown) {
      loader.rel = 'preload';
      loader.as = 'image';
      loader.href = url;
      document.head.appendChild(loader);
    } else {
      loader.src = url;
    }
  }
  function iconWant(want, el, live) {
    var file = el.getAttribute('data-icon-lazy');
    if (!file || iconFiles[file]) return;
    var w = want[file];
    if (!w) {
      var m = /--icon-url:\s*url\(\s*(['"]?)(.*?)\1\s*\)/.exec(el.getAttribute('style') || '');
      w = want[file] = { url: m ? m[2] : '', shown: false };
    }
    if (live && !w.shown && el.getClientRects().length) w.shown = true;
  }
  function iconStart(want) {
    for (var file in want) {
      if (want[file].url) iconFetch(file, want[file].url, want[file].shown);
    }
  }
  // Reads every icon first, then starts the fetches.
  function iconSweep() {
    var want = {};
    var els = document.querySelectorAll('.sugar-icon[data-icon-lazy]');
    for (var i = 0; i < els.length; i++) iconWant(want, els[i], true);
    var tpls = document.querySelectorAll('template');
    for (var t = 0; t < tpls.length; t++) {
      var inner = tpls[t].content.querySelectorAll('.sugar-icon[data-icon-lazy]');
      for (var k = 0; k < inner.length; k++) iconWant(want, inner[k], false);
    }
    iconStart(want);
  }
  var iconIO = new IntersectionObserver(function (entries) {
    var want = {};
    for (var i = 0; i < entries.length; i++) {
      if (!entries[i].isIntersecting) continue;
      iconIO.unobserve(entries[i].target);
      iconWant(want, entries[i].target, true);
    }
    iconStart(want);
  });
  function iconWatch(el) {
    if (iconFiles[el.getAttribute('data-icon-lazy')]) return;
    if (!iconLoaded) {
      iconIO.observe(el);
      return;
    }
    var want = {};
    iconWant(want, el, true);
    iconStart(want);
  }
  // A first-screen icon inside something closed (the cart drawer, a video's
  // controls) has a preload its mask has not used, and WebKit warns about each
  // one three seconds after load. An image takes it over without a second
  // download.
  function iconClaimPreloads() {
    var links = document.querySelectorAll('.sugar-icon > link[rel="preload"], .svg-icon--custom > link[rel="preload"]');
    var idle = {};
    for (var i = 0; i < links.length; i++) {
      var icon = links[i].parentNode;
      if (icon.style.display !== 'none' && !icon.getClientRects().length) idle[links[i].href] = true;
    }
    for (var url in idle) {
      var img = new Image();
      img.crossOrigin = 'anonymous';
      img.src = url;
    }
  }
  function iconAfterLoad() {
    iconLoaded = true;
    iconIO.disconnect();
    iconClaimPreloads();
    later(iconSweep, { timeout: 2000 });
  }
  if (iconLoaded) iconAfterLoad();
  else window.addEventListener('load', iconAfterLoad);
  if (pageLoaded) rowsAfterLoad();
  else window.addEventListener('load', rowsAfterLoad);

  function observe(root) {
    var found = (root || document).querySelectorAll('img[loading="lazy" i], .sugar-icon[data-icon-lazy], .splide');
    for (var j = 0; j < found.length; j++) {
      if (found[j].tagName === 'IMG') watch(found[j]);
      else if (found[j].hasAttribute('data-icon-lazy')) iconWatch(found[j]);
      else watchRow(found[j]);
    }
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { observe(); });
  } else {
    observe();
  }
  new MutationObserver(function (mutations) {
    for (var m = 0; m < mutations.length; m++) {
      for (var n = 0; n < mutations[m].addedNodes.length; n++) {
        var node = mutations[m].addedNodes[n];
        if (node.nodeType !== 1) continue;
        if (node.tagName === 'IMG' && node.loading === 'lazy') watch(node);
        else if (node.hasAttribute('data-icon-lazy')) iconWatch(node);
        else {
          if (node.classList.contains('splide')) watchRow(node);
          observe(node);
        }
        if (!pageLoaded || (node.tagName !== 'IMG' && node.tagName !== 'VIDEO' && !node.querySelector('img, video'))) continue;
        var slider = node.parentElement && node.parentElement.closest('.splide');
        if (slider) watchRow(slider);
        else sortSoon(node.matches('img, video') ? [node] : node.querySelectorAll('img[loading="lazy" i], video[data-poster]:not([poster])'));
      }
    }
  }).observe(document.body || document.documentElement, { childList: true, subtree: true });
})();
